import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { formatNotesForExport, getPlanNotes } from "../../../utils/earningPlanNotes";
import {
  buildReferralPeriodsSheetRows,
  buildRoiPeriodsSheetRows,
  getPayoutPeriodsExportFields,
  getPayoutPeriodsSummary,
  getUserPlanExportIdentity,
  PayoutPeriodsSummary,
  PayoutPeriodsTable,
} from "./stakeReportShared";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./StakingStakesPage.css";
import "./StakingFullReportPage.css";

const DEFAULT_PAGE_LIMIT = 50;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "staking users";

const SUBSCRIPTION_STATUS_OPTIONS = ["ACTIVE", "COMPLETED", "CANCELLED"];

const SUBSCRIPTION_TYPE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One Time" },
];

const unwrapForForm = (v) => {
  const x = extractDecimal(v);
  return x != null ? String(x) : v != null && v !== "" ? String(v) : "";
};

const getSubscriptionId = (row) => row?._id ?? row?.id ?? null;

const extractDecimal = (v) => {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getRowUserId = (row) =>
  row?.user_id ?? row?.user?.id ?? row?.user?.user_id ?? row?.user?._id ?? null;

const getRowUserUuid = (row) =>
  row?.user?.uuid ?? row?.user_uuid ?? row?.uuid ?? null;

const getUserPayoutStatus = (row) => {
  const raw = row?.user?.payout_status;
  if (raw == null || raw === "") return "active";
  return String(raw).toLowerCase() === "inactive" ? "inactive" : "active";
};

const getPlanPayoutStatus = (row) => {
  const raw = row?.payout_status;
  if (raw == null || raw === "") return "active";
  return String(raw).toLowerCase() === "inactive" ? "inactive" : "active";
};

const formatPayoutStatusLabel = (status) =>
  status === "inactive" ? "Inactive" : "Active";

const formatPeriodsCell = (summary) =>
  summary.total > 0 ? `${summary.paid}/${summary.total} paid` : "—";

const getUsdtValue = (row) => row?.usdt_value ?? row?.usdtValue ?? null;

const canCancelStake = (row) => {
  const st = String(row?.status ?? "").toUpperCase();
  const stakingId = row?._id ?? row?.id;
  const userId = getRowUserId(row);
  return st === "ACTIVE" && Boolean(stakingId && userId);
};

const StakingStakesPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [planId, setPlanId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutDetail, setPayoutDetail] = useState(null);
  /** List row from user-plans — carries cancellation fields not always returned by payout-history. */
  const [payoutListRow, setPayoutListRow] = useState(null);
  const [payoutError, setPayoutError] = useState("");
  const [payoutUpdatingUserId, setPayoutUpdatingUserId] = useState("");
  const [planPayoutUpdatingId, setPlanPayoutUpdatingId] = useState("");
  const [payoutStatusMessage, setPayoutStatusMessage] = useState({ type: "", text: "" });
  const [exportLoading, setExportLoading] = useState(false);
  const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
  const [cancelLoadingId, setCancelLoadingId] = useState("");
  const [cancelSuccess, setCancelSuccess] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    notes: "",
    invested_amount: "",
    expected_return: "",
    duration_days: "",
    status: "ACTIVE",
    subscription_type: "monthly",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editUsdtAmount, setEditUsdtAmount] = useState("");
  const [editConvertLoading, setEditConvertLoading] = useState(false);
  const [editConvertResult, setEditConvertResult] = useState(null);
  const [editConvertError, setEditConvertError] = useState("");
  const [editHistoryRow, setEditHistoryRow] = useState(null);
  const [editHistoryData, setEditHistoryData] = useState([]);
  const [editHistoryLoading, setEditHistoryLoading] = useState(false);
  const [editHistoryError, setEditHistoryError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, statusFilter, currencyId, planId, from, to, pageLimit]);

  const buildFilterParams = useCallback(
    (pageNum, limitNum) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedUser) params.user = debouncedUser;
      if (
        statusFilter === "ACTIVE" ||
        statusFilter === "COMPLETED" ||
        statusFilter === "CANCELLED"
      ) {
        params.status = statusFilter;
      } else if (statusFilter === "all") {
        params.status = "all";
      }
      if (currencyId.trim()) params.currency_id = currencyId.trim();
      if (planId.trim()) params.plan_id = planId.trim();
      if (from) {
        const d = new Date(from);
        if (!Number.isNaN(d.getTime())) params.from = d.toISOString();
      }
      if (to) {
        const d = new Date(to);
        if (!Number.isNaN(d.getTime())) params.to = d.toISOString();
      }
      return params;
    },
    [debouncedUser, statusFilter, currencyId, planId, from, to]
  );

  const buildParams = useCallback(
    () => buildFilterParams(page, pageLimit),
    [buildFilterParams, page, pageLimit]
  );

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getGlobalUserEarningPlans(buildParams());
        const list = Array.isArray(res?.data) ? res.data : [];
        setPlans(list);
        setTotalPages(res?.pagination?.total_pages || 1);
        setTotalCount(
          typeof res?.pagination?.total === "number" ? res.pagination.total : null
        );
        if (res?.success === false && !list.length) {
          setError(res?.message || "Failed to load subscriptions.");
        }
      } catch (e) {
        console.error(e);
        setPlans([]);
        setError("Failed to load subscriptions.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [buildParams]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
      : "—";

  const formatDec = (v) => {
    const x = extractDecimal(v);
    return x != null ? formatNum(x) : "—";
  };

  const formatNumPlain = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", {
          maximumFractionDigits: 12,
          useGrouping: false,
        })
      : "—";

  const formatDecPlain = (v) => {
    const x = extractDecimal(v);
    return x != null ? formatNumPlain(x) : "—";
  };

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  const planLabel = (plan) => {
    if (!plan) return "—";
    const c = plan.currency ?? plan.short_name ?? plan.currency_short_name;
    const d = plan.duration_days;
    const r = plan.return_percentage;
    if (c != null && d != null && r != null) {
      return `${c} · ${d}d · ${r}%`;
    }
    return c || plan._id || plan.id || "—";
  };

  const closePayoutModal = () => {
    setPayoutModalOpen(false);
    setPayoutDetail(null);
    setPayoutListRow(null);
    setPayoutError("");
  };

  const openPayoutHistory = async (row) => {
    const userId = row.user_id ?? row.user?.id ?? row.user?.user_id;
    const stakingId = row._id ?? row.id;
    if (!userId || !stakingId) {
      window.alert("Missing user or stake id for this row.");
      return;
    }
    setPayoutModalOpen(true);
    setPayoutLoading(true);
    setPayoutDetail(null);
    setPayoutListRow(row);
    setPayoutError("");
    try {
      const res = await AdminService.getEarningUserPlanPayoutHistory({
        userId: String(userId),
        stakingId: String(stakingId),
      });
      if (res?.success && res.data) {
        setPayoutDetail(res.data);
      } else {
        setPayoutError(res?.message || "Could not load payout history.");
      }
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.message || e?.message || "Request failed.");
    } finally {
      setPayoutLoading(false);
    }
  };

  const formatDateExport = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(iso);
    }
  };

  const rowToExport = (row) => {
    const u = row.user;
    const plan = row.plan;
    const invested = row.invested_amount ?? row.investedAmount ?? row.amount;
    const expected = row.expected_return ?? row.expectedReturn ?? row.expected_reward;
    const start = row.start_date ?? row.startDate;
    const end = row.end_date ?? row.endDate;
    return {
      UUID: u?.uuid ?? "",
      Name: userFullName(u) === "—" ? "" : userFullName(u),
      Email: u?.email ?? "",
      Phone: u?.phone ?? "",
      Plan: planLabel(plan) === "—" ? "" : planLabel(plan),
      Invested: formatDec(invested) === "—" ? "" : formatDec(invested),
      "USDT value": formatDec(getUsdtValue(row)) === "—" ? "" : formatDec(getUsdtValue(row)),
      "Expected Return": formatDec(expected) === "—" ? "" : formatDec(expected),
      Status: row.status ?? "",
      Notes: formatNotesForExport(row),
      "User Payout Status": formatPayoutStatusLabel(getUserPayoutStatus(row)),
      "Plan Payout Status": formatPayoutStatusLabel(getPlanPayoutStatus(row)),
      ...getPayoutPeriodsExportFields(row),
      Start: formatDateExport(start),
      End: formatDateExport(end),
    };
  };

  const fetchAllFilteredPlans = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getGlobalUserEarningPlans(
        buildFilterParams(currentPage, EXPORT_BATCH)
      );
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setPayoutStatusMessage({ type: "", text: "" });
    try {
      const allRows = await fetchAllFilteredPlans();
      if (!allRows.length) {
        setPayoutStatusMessage({
          type: "error",
          text: "No subscriptions match your filters to export.",
        });
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const roiSheetRows = buildRoiPeriodsSheetRows(allRows, getUserPlanExportIdentity);
      if (roiSheetRows.length > 0) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(roiSheetRows),
          "ROI Periods"
        );
      }

      const referralSheetRows = buildReferralPeriodsSheetRows(allRows, getUserPlanExportIdentity);
      if (referralSheetRows.length > 0) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(referralSheetRows),
          "Referral Periods"
        );
      }

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `staking_users_${dateStamp}.xlsx`);

      setPayoutStatusMessage({
        type: "success",
        text: `Exported ${allRows.length} row(s) to Excel.`,
      });
      setTimeout(() => setPayoutStatusMessage({ type: "", text: "" }), 4000);
    } catch (e) {
      console.error(e);
      setPayoutStatusMessage({
        type: "error",
        text: e?.message || "Failed to export Excel file.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const pageStart = plans.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = plans.length === 0 ? 0 : (page - 1) * pageLimit + plans.length;

  const closeCancelConfirm = () => {
    if (cancelLoadingId) return;
    setCancelConfirmRow(null);
  };

  const openCancelConfirm = (row) => {
    if (!canCancelStake(row)) return;
    setCancelConfirmRow(row);
  };

  const resetEditZilConvert = () => {
    setEditUsdtAmount("");
    setEditConvertResult(null);
    setEditConvertError("");
    setEditConvertLoading(false);
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditRow(null);
    setEditError("");
    resetEditZilConvert();
  };

  const handleEditZilConvert = async () => {
    const usdt_amount = Number(editUsdtAmount.trim());
    if (!editUsdtAmount.trim() || !Number.isFinite(usdt_amount) || usdt_amount <= 0) {
      setEditConvertError("Enter a valid USDT amount greater than 0.");
      return;
    }
    setEditConvertLoading(true);
    setEditConvertError("");
    setEditConvertResult(null);
    try {
      const res = await AdminService.convertZilPricing({ usdt_amount });
      if (res?.success && res.data) {
        setEditConvertResult(res.data);
      } else {
        setEditConvertError(res?.message || "Conversion failed.");
      }
    } catch (err) {
      setEditConvertError(err?.response?.data?.message || err?.message || "Conversion failed.");
    } finally {
      setEditConvertLoading(false);
    }
  };

  const applyEditConvertedZil = () => {
    if (!editConvertResult) return;
    const zil =
      editConvertResult.zil_amount ??
      editConvertResult.zilAmount ??
      editConvertResult.amount ??
      editConvertResult.converted_amount;
    if (zil == null || zil === "") {
      setEditConvertError("No ZILN amount in conversion response.");
      return;
    }
    updateEditForm("invested_amount", String(zil));
    setEditConvertError("");
  };

  const closeEditHistory = () => {
    setEditHistoryRow(null);
    setEditHistoryData([]);
    setEditHistoryError("");
  };

  const openEditHistory = async (row) => {
    const subscriptionId = getSubscriptionId(row);
    if (!subscriptionId) return;
    setEditHistoryRow(row);
    setEditHistoryLoading(true);
    setEditHistoryData([]);
    setEditHistoryError("");
    try {
      const res = await AdminService.getEarningUserPlanEditHistory(String(subscriptionId));
      if (res?.success) {
        setEditHistoryData(Array.isArray(res.data) ? res.data : []);
      } else {
        setEditHistoryError(res?.message || "Could not load edit history.");
      }
    } catch (e) {
      setEditHistoryError(e?.response?.data?.message || e?.message || "Request failed.");
    } finally {
      setEditHistoryLoading(false);
    }
  };

  const openEditModal = (row) => {
    const subscriptionId = getSubscriptionId(row);
    const userId = getRowUserId(row);
    if (!subscriptionId || !userId) return;
    const plan = row.plan;
    const rawType = String(
      row.subscription_type ?? row.subscriptionType ?? plan?.return_type ?? "monthly"
    ).toLowerCase();
    const subscription_type = SUBSCRIPTION_TYPE_OPTIONS.some((o) => o.value === rawType)
      ? rawType
      : "monthly";
    const rawStatus = String(row.status ?? "ACTIVE").toUpperCase();
    const status = SUBSCRIPTION_STATUS_OPTIONS.includes(rawStatus) ? rawStatus : "ACTIVE";

    setEditRow(row);
    setEditError("");
    resetEditZilConvert();
    setEditForm({
      notes: getPlanNotes(row),
      invested_amount: unwrapForForm(
        row.invested_amount ?? row.investedAmount ?? row.amount
      ),
      expected_return: unwrapForForm(
        row.expected_return ?? row.expectedReturn ?? row.expected_reward
      ),
      duration_days: String(row.duration_days ?? row.durationDays ?? plan?.duration_days ?? ""),
      status,
      subscription_type,
    });
  };

  const updateEditForm = (key, value) => {
    setEditError("");
    setEditForm((f) => ({ ...f, [key]: value }));
  };

  const handleEditSubscriptionSave = async (e) => {
    e?.preventDefault?.();
    const row = editRow;
    if (!row) return;
    const subscriptionId = String(getSubscriptionId(row));
    setEditError("");

    const userId = String(getRowUserId(row) ?? "");
    const invested_amount = Number(editForm.invested_amount);
    const previousInvested = extractDecimal(
      row.invested_amount ?? row.investedAmount ?? row.amount
    );
    const notes = editForm.notes.trim();
    const previousNotes = getPlanNotes(row);
    const status = editForm.status;
    const previousStatus = String(row.status ?? "ACTIVE").toUpperCase();

    if (!userId) {
      setEditError("User ID is missing for this subscription.");
      return;
    }
    if (!Number.isFinite(invested_amount) || invested_amount <= 0) {
      setEditError("Invested amount must be greater than 0.");
      return;
    }
    if (!SUBSCRIPTION_STATUS_OPTIONS.includes(status)) {
      setEditError("Select a valid status.");
      return;
    }
    const investedChanged =
      previousInvested == null || invested_amount !== previousInvested;
    const statusChanged = status !== previousStatus;
    const notesChanged = notes !== previousNotes;
    if (!investedChanged && !statusChanged && !notesChanged) {
      setEditError("No changes to save.");
      return;
    }

    const body = {
      userId,
      notes,
      invested_amount,
      status,
    };

    setEditSaving(true);
    setPayoutStatusMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateEarningUserPlan(subscriptionId, body);
      if (res?.success) {
        const updated = res.data || {};
        setPlans((prev) =>
          prev.map((r) => {
            const rid = getSubscriptionId(r);
            if (String(rid) !== subscriptionId) return r;
            return {
              ...r,
              ...updated,
              user_id: updated.user_id ?? updated.userId ?? userId,
              invested_amount: updated.invested_amount ?? invested_amount,
              status: updated.status ?? status,
              notes: updated.notes ?? notes,
              ...(updated.expected_return != null && {
                expected_return: updated.expected_return,
              }),
            };
          })
        );
        closeEditModal();
        setPayoutStatusMessage({
          type: "success",
          text: res?.message || "Subscription updated successfully.",
        });
        setTimeout(() => setPayoutStatusMessage({ type: "", text: "" }), 5000);
      } else {
        setEditError(res?.message || "Could not update subscription.");
      }
    } catch (err) {
      console.error(err);
      setEditError(
        err?.response?.data?.message || err?.message || "Could not update subscription."
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    const row = cancelConfirmRow;
    if (!row) return;
    const stakingId = String(row._id ?? row.id);
    const userId = String(getRowUserId(row));
    setCancelLoadingId(stakingId);
    setPayoutStatusMessage({ type: "", text: "" });
    try {
      const res = await AdminService.cancelEarningSubscriptionByAdmin({
        stakingId,
        userId,
      });
      if (res?.success) {
        setPlans((prev) =>
          prev.map((r) => {
            const rid = r._id ?? r.id;
            if (String(rid) !== stakingId) return r;
            return { ...r, status: "CANCELLED" };
          })
        );
        setCancelConfirmRow(null);
        setCancelSuccess({
          message: res.message || "Staking plan cancelled successfully.",
          data: res.data || {},
          row,
        });
        setPayoutStatusMessage({
          type: "success",
          text: res.message || "Staking plan cancelled successfully.",
        });
        setTimeout(() => setPayoutStatusMessage({ type: "", text: "" }), 5000);
      } else {
        setPayoutStatusMessage({
          type: "error",
          text: res?.message || "Could not cancel staking plan.",
        });
      }
    } catch (e) {
      console.error(e);
      setPayoutStatusMessage({
        type: "error",
        text: e?.response?.data?.message || e?.message || "Could not cancel staking plan.",
      });
    } finally {
      setCancelLoadingId("");
    }
  };

  const handlePlanPayoutStatusChange = async (row, nextStatus) => {
    const subscriptionId = getSubscriptionId(row);
    const rowUserId = getRowUserId(row);
    if (!subscriptionId) return;
    const subscriptionIdStr = String(subscriptionId);
    setPlanPayoutUpdatingId(subscriptionIdStr);
    setPayoutStatusMessage({ type: "", text: "" });
    try {
      const body = { payout_status: nextStatus };
      if (rowUserId) body.userId = String(rowUserId);
      const res = await AdminService.updateEarningUserPlanPayoutStatus(subscriptionIdStr, body);
      if (res?.success) {
        const updatedStatus =
          res?.data?.payout_status != null
            ? String(res.data.payout_status).toLowerCase() === "inactive"
              ? "inactive"
              : "active"
            : nextStatus;
        setPlans((prev) =>
          prev.map((planRow) => {
            const pid = getSubscriptionId(planRow);
            if (String(pid) !== subscriptionIdStr) return planRow;
            return { ...planRow, payout_status: updatedStatus };
          })
        );
        setPayoutStatusMessage({
          type: "success",
          text: res?.message || `Plan payout status set to ${formatPayoutStatusLabel(updatedStatus)}.`,
        });
        setTimeout(() => setPayoutStatusMessage({ type: "", text: "" }), 4000);
      } else {
        setPayoutStatusMessage({
          type: "error",
          text: res?.message || "Failed to update plan payout status.",
        });
      }
    } catch (e) {
      console.error(e);
      setPayoutStatusMessage({
        type: "error",
        text:
          e?.response?.data?.message || e?.message || "Failed to update plan payout status.",
      });
    } finally {
      setPlanPayoutUpdatingId("");
    }
  };

  const handlePayoutStatusChange = async (userId, nextStatus) => {
    if (!userId) return;
    const userIdStr = String(userId);
    setPayoutUpdatingUserId(userIdStr);
    setPayoutStatusMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateUserPayoutStatus(userIdStr, nextStatus);
      if (res?.success) {
        setPlans((prev) =>
          prev.map((row) => {
            const rid = getRowUserId(row);
            if (String(rid) !== userIdStr) return row;
            return {
              ...row,
              user: row.user ? { ...row.user, payout_status: nextStatus } : row.user,
            };
          })
        );
        setPayoutStatusMessage({
          type: "success",
          text: res?.message || `Payout status set to ${nextStatus}.`,
        });
        setTimeout(() => setPayoutStatusMessage({ type: "", text: "" }), 4000);
      } else {
        setPayoutStatusMessage({
          type: "error",
          text: res?.message || "Failed to update payout status.",
        });
      }
    } catch (e) {
      console.error(e);
      setPayoutStatusMessage({
        type: "error",
        text: e?.response?.data?.message || e?.message || "Failed to update payout status.",
      });
    } finally {
      setPayoutUpdatingUserId("");
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>User Stakes</span>
          </p>
          <h1 className="dashboard-hero__title">User Stakes</h1>
          <p className="dashboard-hero__subtitle">
            All users’ earning subscriptions (admin)
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-coins" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-coins" />
              </span>
              <h2 className="openorders-section__title">Subscriptions</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{plans.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="staking-stakes__export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered subscriptions as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}
          {payoutStatusMessage.text && (
            <p
              style={{
                color: payoutStatusMessage.type === "error" ? "#dc2626" : "#166534",
                padding: "0 0.5rem 0.75rem",
                margin: 0,
                background:
                  payoutStatusMessage.type === "error"
                    ? "rgba(220, 38, 38, 0.08)"
                    : "rgba(22, 101, 52, 0.08)",
                borderRadius: 8,
              }}
              role="status"
            >
              {payoutStatusMessage.text}
            </p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 220px" }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 160px" }}
            >
              <option value="">Status (default)</option>
              <option value="all">all</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
            <input
              type="text"
              placeholder="Currency ObjectId"
              value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Plan (package) ObjectId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              From
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              To
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label className="staking-page-limit">
              <span>Rows per page</span>
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="openorders-search-input staking-page-limit__select"
                disabled={loading}
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : plans.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Plan</th>
                      <th>Invested</th>
                      <th>USDT value</th>
                      <th>Expected</th>
                      <th>Status</th>
                      <th>User payout status</th>
                      <th>Plan payout status</th>
                      <th>ROI periods</th>
                      <th>Referral periods</th>
                      <th>Start</th>
                      <th>End</th>
                      <th style={{ width: "1%", whiteSpace: "nowrap" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((row) => {
                      const rid = row._id || row.id;
                      const uid = row.user?.id || row.user_id;
                      const u = row.user;
                      const uuidStr = u?.uuid ?? "—";
                      const plan = row.plan;
                      const invested =
                        row.invested_amount ?? row.investedAmount ?? row.amount;
                      const usdtValue = getUsdtValue(row);
                      const expected =
                        row.expected_return ?? row.expectedReturn ?? row.expected_reward;
                      const start = row.start_date ?? row.startDate;
                      const end = row.end_date ?? row.endDate;
                      const st = row.status ?? "—";
                      const rowUserId = getRowUserId(row);
                      const payoutStatus = getUserPayoutStatus(row);
                      const payoutStatusLabel = formatPayoutStatusLabel(payoutStatus);
                      const planPayoutStatus = getPlanPayoutStatus(row);
                      const planPayoutStatusLabel = formatPayoutStatusLabel(planPayoutStatus);
                      const roiPeriods = getPayoutPeriodsSummary(row.roi_payout_periods);
                      const referralPeriods = getPayoutPeriodsSummary(row.referral_payout_periods);
                      const isPayoutUpdating = payoutUpdatingUserId === String(rowUserId);
                      const isPlanPayoutUpdating = planPayoutUpdatingId === String(rid);
                      const canViewPayout = Boolean(rid && rowUserId);
                      const showCancelPlan = canCancelStake(row);
                      const isCancelLoading = cancelLoadingId === String(rid);
                      const canEditStake = Boolean(rid && rowUserId);
                      const isEditOpen = editRow && String(getSubscriptionId(editRow)) === String(rid);
                      return (
                        <tr key={rid}>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{
                                  color: "#dc2626",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                {uuidStr}
                              </Link>
                            ) : (
                              uuidStr
                            )}
                          </td>
                          <td>{userFullName(u)}</td>
                          <td>{u?.email ?? "—"}</td>
                          <td>{u?.phone ?? "—"}</td>
                          <td>
                            <strong title={plan?._id || plan?.id}>{planLabel(plan)}</strong>
                          </td>
                          <td>{formatDec(invested)}</td>
                          <td>{formatDec(usdtValue)}</td>
                          <td>{formatDec(expected)}</td>
                          <td>
                            <span
                              className={`cp-badge cp-badge--${
                                String(st).toUpperCase() === "ACTIVE" ? "global" : "local"
                              }`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {st}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`staking-stakes__payout-badge staking-stakes__payout-badge--${payoutStatus}`}
                            >
                              {payoutStatusLabel}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`staking-stakes__payout-badge staking-stakes__payout-badge--${planPayoutStatus}`}
                            >
                              {planPayoutStatusLabel}
                            </span>
                          </td>
                          <td title={`${roiPeriods.paid} paid, ${roiPeriods.unpaid} unpaid`}>
                            {formatPeriodsCell(roiPeriods)}
                          </td>
                          <td title={`${referralPeriods.paid} paid, ${referralPeriods.unpaid} unpaid`}>
                            {formatPeriodsCell(referralPeriods)}
                          </td>
                          <td>{formatDate(start)}</td>
                          <td>{formatDate(end)}</td>
                          <td>
                            <div className="staking-stakes__actions">
                              <button
                                type="button"
                                className="staking-stakes__view-btn"
                                onClick={() => openPayoutHistory(row)}
                                disabled={!canViewPayout}
                                title="Payout history"
                              >
                                <i className="fas fa-eye" /> View
                              </button>
                              {canEditStake && (
                                <button
                                  type="button"
                                  className="staking-stakes__edit-btn"
                                  onClick={() => openEditModal(row)}
                                  disabled={editSaving && isEditOpen}
                                  title="Edit subscription"
                                >
                                  <i className="fas fa-pen" /> Edit
                                </button>
                              )}
                              {canEditStake && (
                                <button
                                  type="button"
                                  className="staking-stakes__history-btn"
                                  onClick={() => openEditHistory(row)}
                                  title="View edit history"
                                >
                                  <i className="fas fa-history" /> History
                                </button>
                              )}
                              {showCancelPlan && (
                                <button
                                  type="button"
                                  className="staking-stakes__cancel-btn"
                                  onClick={() => openCancelConfirm(row)}
                                  disabled={isCancelLoading}
                                  title="Cancel this user's active staking plan"
                                >
                                  <i
                                    className={`fas ${
                                      isCancelLoading ? "fa-spinner fa-spin" : "fa-ban"
                                    }`}
                                  />{" "}
                                  Cancel plan
                                </button>
                              )}
                              {(rowUserId || rid) && (
                                <div className="staking-stakes__payout-actions">
                                  {rowUserId &&
                                    (payoutStatus === "active" ? (
                                      <button
                                        type="button"
                                        className="staking-stakes__payout-btn staking-stakes__payout-btn--off"
                                        onClick={() =>
                                          handlePayoutStatusChange(rowUserId, "inactive")
                                        }
                                        disabled={isPayoutUpdating}
                                        title="Pause user payouts"
                                      >
                                        <i
                                          className={`fas ${
                                            isPayoutUpdating
                                              ? "fa-spinner fa-spin"
                                              : "fa-pause-circle"
                                          }`}
                                        />{" "}
                                        Set Inactive
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="staking-stakes__payout-btn staking-stakes__payout-btn--on"
                                        onClick={() =>
                                          handlePayoutStatusChange(rowUserId, "active")
                                        }
                                        disabled={isPayoutUpdating}
                                        title="Enable user payouts"
                                      >
                                        <i
                                          className={`fas ${
                                            isPayoutUpdating
                                              ? "fa-spinner fa-spin"
                                              : "fa-play-circle"
                                          }`}
                                        />{" "}
                                        Set Active
                                      </button>
                                    ))}
                                  {rid &&
                                    (planPayoutStatus === "active" ? (
                                      <button
                                        type="button"
                                        className="staking-stakes__payout-btn staking-stakes__payout-btn--off staking-stakes__payout-btn--plan"
                                        onClick={() =>
                                          handlePlanPayoutStatusChange(row, "inactive")
                                        }
                                        disabled={isPlanPayoutUpdating}
                                        title="Pause payouts for this subscription plan"
                                      >
                                        <i
                                          className={`fas ${
                                            isPlanPayoutUpdating
                                              ? "fa-spinner fa-spin"
                                              : "fa-pause-circle"
                                          }`}
                                        />{" "}
                                        Plan payout Inactive
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="staking-stakes__payout-btn staking-stakes__payout-btn--on staking-stakes__payout-btn--plan"
                                        onClick={() =>
                                          handlePlanPayoutStatusChange(row, "active")
                                        }
                                        disabled={isPlanPayoutUpdating}
                                        title="Enable payouts for this subscription plan"
                                      >
                                        <i
                                          className={`fas ${
                                            isPlanPayoutUpdating
                                              ? "fa-spinner fa-spin"
                                              : "fa-play-circle"
                                          }`}
                                        />{" "}
                                        Plan payout Active
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalCount > 0 && (
                <div
                  className="staking-stakes__pagination"
                  style={{ padding: "0.75rem 0", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
                >
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="openorders-stat" style={{ margin: 0 }}>
                    Showing {pageStart}–{pageEnd}
                    {totalCount != null ? ` of ${totalCount}` : ""}
                    {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                  </span>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No subscriptions match your filters.</p>
          )}
        </section>
      </div>

      {editRow && (
        <div
          className="staking-payout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staking-edit-sub-title"
          onClick={closeEditModal}
        >
          <div
            className="staking-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="staking-edit-modal__head">
              <h2 id="staking-edit-sub-title">Edit subscription</h2>
              <p>
                {userFullName(editRow.user)}
                {editRow.user?.email ? ` · ${editRow.user.email}` : ""}
                {" · "}
                {planLabel(editRow.plan)}
              </p>
              <p className="staking-edit-modal__hint">
                You can update invested amount, status, and admin notes.
              </p>
            </div>
            <form className="staking-edit-modal__form" onSubmit={handleEditSubscriptionSave}>
              <div className="staking-edit-modal__body">
                <div className="staking-edit-form-grid">
                  <label className="staking-edit-field staking-edit-field--full staking-edit-field--readonly">
                    <span>User UUID</span>
                    <input
                      type="text"
                      value={getRowUserUuid(editRow) ?? "—"}
                      disabled
                      readOnly
                    />
                  </label>
                  <label className="staking-edit-field staking-edit-field--readonly">
                    <span>Duration (days)</span>
                    <input type="text" value={editForm.duration_days} disabled readOnly />
                  </label>
                  <label className="staking-edit-field staking-edit-field--readonly">
                    <span>Subscription type</span>
                    <input
                      type="text"
                      value={
                        SUBSCRIPTION_TYPE_OPTIONS.find(
                          (o) => o.value === editForm.subscription_type
                        )?.label ?? editForm.subscription_type
                      }
                      disabled
                      readOnly
                    />
                  </label>
                  <label className="staking-edit-field">
                    <span>Invested amount</span>
                    <input
                      type="number"
                      step="any"
                      min={0.00000001}
                      value={editForm.invested_amount}
                      onChange={(e) => updateEditForm("invested_amount", e.target.value)}
                      required
                    />
                  </label>
                  <label className="staking-edit-field staking-edit-field--readonly">
                    <span>Expected return</span>
                    <input
                      type="text"
                      value={editForm.expected_return}
                      disabled
                      readOnly
                    />
                  </label>
                  <label className="staking-edit-field">
                    <span>Status</span>
                    <select
                      value={editForm.status}
                      onChange={(e) => updateEditForm("status", e.target.value)}
                    >
                      {SUBSCRIPTION_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="staking-edit-field staking-edit-field--full">
                    <span>Notes</span>
                    <textarea
                      rows={3}
                      value={editForm.notes}
                      onChange={(e) => updateEditForm("notes", e.target.value)}
                      placeholder="e.g. Adjusted after admin review"
                    />
                  </label>
                </div>
                {(() => {
                  const planCcy = String(
                    editRow.plan?.currency ?? editRow.plan?.currency_short_name ?? editRow.plan?.short_name ?? editRow.currency ?? ""
                  ).trim().toUpperCase();
                  if (planCcy !== "ZILN") return null;
                  const convertedZil =
                    editConvertResult?.zil_amount ??
                    editConvertResult?.zilAmount ??
                    editConvertResult?.amount ??
                    editConvertResult?.converted_amount;
                  return (
                    <div className="staking-edit-zil-convert">
                      <div className="staking-edit-zil-convert__title">
                        <i className="fas fa-exchange-alt" aria-hidden />
                        USDT → ZILN converter
                      </div>
                      <p className="staking-edit-zil-convert__sub">
                        Enter USDT value to calculate the equivalent ZILN amount and apply it as invested amount.
                      </p>
                      <div className="staking-edit-zil-convert__row">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="staking-edit-zil-convert__input"
                          placeholder="USDT amount, e.g. 100"
                          value={editUsdtAmount}
                          onChange={(e) => { setEditUsdtAmount(e.target.value); setEditConvertError(""); }}
                          disabled={editConvertLoading}
                        />
                        <button
                          type="button"
                          className="staking-edit-zil-convert__btn"
                          onClick={handleEditZilConvert}
                          disabled={editConvertLoading || !editUsdtAmount.trim()}
                        >
                          {editConvertLoading
                            ? <><i className="fas fa-spinner fa-spin" /> Converting…</>
                            : <><i className="fas fa-calculator" /> Convert</>}
                        </button>
                      </div>
                      {editConvertError && (
                        <p className="staking-edit-zil-convert__error" role="alert">{editConvertError}</p>
                      )}
                      {convertedZil != null && convertedZil !== "" && (
                        <div className="staking-edit-zil-convert__result">
                          <div className="staking-edit-zil-convert__result-amounts">
                            <span className="staking-edit-zil-convert__from">
                              {formatDecPlain(editUsdtAmount.trim())} USDT
                            </span>
                            <i className="fas fa-arrow-right staking-edit-zil-convert__arrow" />
                            <span className="staking-edit-zil-convert__to">
                              {formatDecPlain(convertedZil)} ZILN
                            </span>
                          </div>
                          <button
                            type="button"
                            className="staking-edit-zil-convert__apply"
                            onClick={applyEditConvertedZil}
                          >
                            <i className="fas fa-check" /> Use as invest amount
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <p className="staking-edit-modal__id">
                  Subscription ID:{" "}
                  <code>{String(getSubscriptionId(editRow))}</code>
                </p>
                {editError ? (
                  <p className="staking-edit-modal__error" role="alert">
                    {editError}
                  </p>
                ) : null}
              </div>
              <div className="staking-edit-modal__foot">
                <button
                  type="button"
                  className="staking-payout-btn-close"
                  onClick={closeEditModal}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="staking-stakes__edit-btn staking-stakes__edit-btn--submit"
                  disabled={editSaving}
                >
                  {editSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden /> Saving…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" aria-hidden /> Save changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cancelConfirmRow && (
        <div
          className="staking-payout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staking-cancel-title"
          onClick={closeCancelConfirm}
        >
          <div className="staking-cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="staking-cancel-modal__head">
              <h2 id="staking-cancel-title">Cancel staking plan</h2>
              <p>
                This will end the subscription, apply the package cancellation fee, and refund the
                remaining balance to the user.
              </p>
            </div>
            <div className="staking-cancel-modal__body">
              <div className="staking-cancel-modal__summary">
                <div>
                  <strong>User:</strong> {userFullName(cancelConfirmRow.user)}{" "}
                  {cancelConfirmRow.user?.email ? `(${cancelConfirmRow.user.email})` : ""}
                </div>
                <div>
                  <strong>Plan:</strong> {planLabel(cancelConfirmRow.plan)}
                </div>
                <div>
                  <strong>Invested:</strong>{" "}
                  {formatDec(
                    cancelConfirmRow.invested_amount ??
                      cancelConfirmRow.investedAmount ??
                      cancelConfirmRow.amount
                  )}
                </div>
              </div>
              <p className="staking-cancel-modal__warn">
                <i className="fas fa-exclamation-triangle" aria-hidden />
                This action cannot be undone. Confirm only if you intend to cancel this stake.
              </p>
            </div>
            <div className="staking-cancel-modal__foot">
              <button
                type="button"
                className="staking-payout-btn-close"
                onClick={closeCancelConfirm}
                disabled={Boolean(cancelLoadingId)}
              >
                Keep plan
              </button>
              <button
                type="button"
                className="staking-stakes__cancel-btn"
                onClick={handleCancelSubscription}
                disabled={Boolean(cancelLoadingId)}
              >
                <i
                  className={`fas ${cancelLoadingId ? "fa-spinner fa-spin" : "fa-check"}`}
                />{" "}
                {cancelLoadingId ? "Cancelling…" : "Yes, cancel plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelSuccess && (
        <div
          className="staking-payout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staking-cancel-success-title"
          onClick={() => setCancelSuccess(null)}
        >
          <div className="staking-cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="staking-cancel-modal__head" style={{ background: "linear-gradient(125deg, #047857 0%, #059669 55%, #065f46 100%)" }}>
              <h2 id="staking-cancel-success-title">Plan cancelled</h2>
              <p>{cancelSuccess.message}</p>
            </div>
            <div className="staking-cancel-modal__body">
              <dl className="staking-cancel-success__grid">
                <div className="staking-cancel-success__item">
                  <dt>Invested amount</dt>
                  <dd>{formatDec(cancelSuccess.data?.investedAmount)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Total paid</dt>
                  <dd>{formatDec(cancelSuccess.data?.totalPaid)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Fee (%)</dt>
                  <dd>
                    {cancelSuccess.data?.feePercent != null
                      ? `${formatDec(cancelSuccess.data.feePercent)}%`
                      : "—"}
                  </dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Fee amount</dt>
                  <dd>{formatDec(cancelSuccess.data?.feeAmount)}</dd>
                </div>
                <div className="staking-cancel-success__item staking-cancel-success__item--refund">
                  <dt>Refund amount</dt>
                  <dd>{formatDec(cancelSuccess.data?.refundAmount)}</dd>
                </div>
              </dl>
            </div>
            <div className="staking-cancel-modal__foot">
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                style={{ margin: 0 }}
                onClick={() => setCancelSuccess(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {editHistoryRow && (
        <div
          className="staking-payout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staking-edit-history-title"
          onClick={closeEditHistory}
        >
          <div
            className="staking-edit-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="staking-edit-history-modal__head">
              <div>
                <p className="staking-payout-head-eyebrow">Subscription</p>
                <h2 id="staking-edit-history-title">Edit history</h2>
                <p className="staking-payout-head-sub">
                  {userFullName(editHistoryRow.user)}
                  {editHistoryRow.user?.email ? ` · ${editHistoryRow.user.email}` : ""}
                  {" · "}
                  {planLabel(editHistoryRow.plan)}
                </p>
              </div>
              <button
                type="button"
                className="staking-payout-modal__close"
                onClick={closeEditHistory}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="staking-edit-history-modal__body">
              {editHistoryLoading ? (
                <div className="staking-payout-loading">
                  <span className="staking-payout-loading__ring" aria-hidden />
                  <p>Loading edit history…</p>
                </div>
              ) : editHistoryError ? (
                <div className="staking-payout-alert" role="alert">
                  <i className="fas fa-exclamation-circle" /> {editHistoryError}
                </div>
              ) : editHistoryData.length === 0 ? (
                <div className="staking-edit-history-empty">
                  <i className="fas fa-history" />
                  <p>No edit history found for this subscription.</p>
                </div>
              ) : (
                <div className="staking-edit-history-list">
                  {editHistoryData.map((entry, idx) => {
                    const changedFields = entry.changes ? Object.entries(entry.changes) : [];
                    return (
                      <div key={entry._id || idx} className="staking-edit-history-entry">
                        <div className="staking-edit-history-entry__header">
                          <div className="staking-edit-history-entry__meta">
                            <span className="staking-edit-history-entry__index">#{editHistoryData.length - idx}</span>
                            <span className="staking-edit-history-entry__date">
                              <i className="fas fa-clock" /> {formatDate(entry.createdAt)}
                            </span>
                          </div>
                          <div className="staking-edit-history-entry__admin">
                            <i className="fas fa-user-shield" />
                            {entry.edited_by?.email_or_phone ?? "—"}
                            {entry.edited_by?.admin_type != null && (
                              <span className="staking-edit-history-entry__admin-type">
                                Type {entry.edited_by.admin_type}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="staking-edit-history-entry__body">
                          {changedFields.length > 0 && (
                            <div className="staking-edit-history-changes">
                              {changedFields.map(([field, diff]) => (
                                <div key={field} className="staking-edit-history-change">
                                  <span className="staking-edit-history-change__field">
                                    {field.replace(/_/g, " ")}
                                  </span>
                                  <span className="staking-edit-history-change__from">
                                    {diff?.from ?? "—"}
                                  </span>
                                  <i className="fas fa-arrow-right staking-edit-history-change__arrow" />
                                  <span className="staking-edit-history-change__to">
                                    {diff?.to ?? "—"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {(entry.prev_status !== entry.new_status) && (
                            <div className="staking-edit-history-status-change">
                              <span className={`cp-badge cp-badge--${String(entry.prev_status).toUpperCase() === "ACTIVE" ? "global" : "local"}`} style={{ fontSize: "0.72rem" }}>
                                {entry.prev_status}
                              </span>
                              <i className="fas fa-arrow-right" style={{ color: "#94a3b8", fontSize: "0.75rem" }} />
                              <span className={`cp-badge cp-badge--${String(entry.new_status).toUpperCase() === "ACTIVE" ? "global" : "local"}`} style={{ fontSize: "0.72rem" }}>
                                {entry.new_status}
                              </span>
                            </div>
                          )}

                          {entry.notes ? (
                            <p className="staking-edit-history-entry__notes">
                              <i className="fas fa-sticky-note" /> {entry.notes}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="staking-payout-modal__foot">
              <button
                type="button"
                className="staking-payout-btn-close"
                onClick={closeEditHistory}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {payoutModalOpen && (
        <div className="staking-payout-overlay" role="dialog" aria-modal="true" aria-labelledby="staking-payout-title">
          <div className="staking-payout-modal">
            <div className="staking-payout-modal__head">
              <div>
                <p className="staking-payout-head-eyebrow">Staking & earning</p>
                <h2 id="staking-payout-title">Payout history</h2>
                <p className="staking-payout-head-sub">
                  {String(payoutListRow?.status ?? payoutDetail?.status ?? "").toUpperCase() ===
                  "CANCELLED"
                    ? "Cancelled subscription · settlement and payout log"
                    : "Live subscription · daily credits to earning wallet"}
                </p>
              </div>
              <button type="button" className="staking-payout-modal__close" onClick={closePayoutModal} aria-label="Close">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="staking-payout-modal__body staking-payout-modal__body--premium">
              {payoutLoading ? (
                <div className="staking-payout-loading">
                  <span className="staking-payout-loading__ring" aria-hidden />
                  <p>Loading payout details…</p>
                </div>
              ) : payoutError ? (
                <div className="staking-payout-alert" role="alert">
                  <i className="fas fa-exclamation-circle" /> {payoutError}
                </div>
              ) : payoutDetail ? (
                <>
                  {(() => {
                    const detailSt = String(payoutDetail.status ?? "").toUpperCase();
                    const listSt = String(payoutListRow?.status ?? "").toUpperCase();
                    const isCancelledModal =
                      listSt === "CANCELLED" || detailSt === "CANCELLED";
                    const pickField = (snake, camel) => {
                      for (const o of [payoutListRow, payoutDetail]) {
                        if (!o) continue;
                        const v = o[snake] ?? o[camel];
                        if (v !== undefined && v !== null) return v;
                      }
                      return undefined;
                    };
                    const usdtValue = pickField("usdt_value", "usdtValue");
                    const cancelledAt = pickField("cancelled_at", "cancelledAt");
                    const cancelFeePct = pickField("cancel_fee_percent", "cancelFeePercent");
                    const cancelFeeAmt = pickField("cancel_fee_amount", "cancelFeeAmount");
                    const refundAmt = pickField("refund_amount", "refundAmount");
                    const ccy = payoutDetail.currency || "";
                    const feePctDisplay =
                      cancelFeePct != null && !Number.isNaN(Number(cancelFeePct))
                        ? `${formatNum(Number(cancelFeePct))}%`
                        : "—";
                    const adminNotes =
                      getPlanNotes(payoutListRow) || getPlanNotes(payoutDetail);

                    return (
                      <>
                  <div className="staking-payout-hero">
                    <div className="staking-payout-hero__top">
                      <div>
                        <p className="staking-payout-hero__eyebrow">Asset</p>
                        <p className="staking-payout-hero__title">
                          {payoutDetail.currency ?? "—"}
                          {payoutDetail.currency_fullname ? (
                            <span className="staking-payout-hero__sub"> · {payoutDetail.currency_fullname}</span>
                          ) : null}
                        </p>
                      </div>
                      <span
                        className={`staking-payout-status-pill staking-payout-status-pill--${
                          isCancelledModal
                            ? "cancelled"
                            : detailSt === "ACTIVE"
                              ? "on"
                              : detailSt === "COMPLETED"
                                ? "done"
                                : "off"
                        }`}
                      >
                        {payoutDetail.status ?? payoutListRow?.status ?? "—"}
                      </span>
                    </div>
                    <p className="staking-payout-hero__wallets">
                      <i className="fas fa-wallet" />
                      {payoutDetail.wallet_type ?? "—"} <span className="staking-payout-hero__arrow">→</span>{" "}
                      {payoutDetail.credited_wallet_type ?? "—"}
                    </p>
                  </div>

                  <div className="staking-payout-notes-card">
                    <h3 className="staking-payout-notes-card__title">
                      <i className="fas fa-sticky-note" aria-hidden /> Admin notes
                    </h3>
                    <p className="staking-payout-notes-card__text">
                      {adminNotes || "—"}
                    </p>
                  </div>

                  {isCancelledModal ? (
                    <div className="staking-payout-cancel-card">
                      <h3 className="staking-payout-cancel-card__title">
                        <i className="fas fa-ban" aria-hidden /> Cancellation
                      </h3>
                      <dl className="staking-payout-cancel-card__dl">
                        <div>
                          <dt>Cancelled at</dt>
                          <dd>{formatDate(cancelledAt)}</dd>
                        </div>
                        <div>
                          <dt>Cancel fee (%)</dt>
                          <dd>{feePctDisplay}</dd>
                        </div>
                        <div>
                          <dt>Cancel fee amount</dt>
                          <dd>
                            {formatDec(cancelFeeAmt)} {ccy}
                          </dd>
                        </div>
                        <div>
                          <dt>Refund amount</dt>
                          <dd>
                            {formatDec(refundAmt)} {ccy}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ) : null}

                  <div className="staking-payout-stat-grid">
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-coins" />
                      </span>
                      <span className="staking-payout-stat__label">Invested</span>
                      <span className="staking-payout-stat__value">
                        {formatDec(payoutDetail.invested_amount)} {payoutDetail.currency || ""}
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-dollar-sign" />
                      </span>
                      <span className="staking-payout-stat__label">USDT value</span>
                      <span className="staking-payout-stat__value">
                        {formatDec(usdtValue)} USDT
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-bullseye" />
                      </span>
                      <span className="staking-payout-stat__label">Expected return</span>
                      <span className="staking-payout-stat__value">
                        {formatDec(payoutDetail.expected_return)} {payoutDetail.currency || ""}
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-check-double" />
                      </span>
                      <span className="staking-payout-stat__label">Total paid</span>
                      <span className="staking-payout-stat__value">
                        {formatDec(payoutDetail.total_paid)} {payoutDetail.currency || ""}
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-hourglass-half" />
                      </span>
                      <span className="staking-payout-stat__label">Remaining</span>
                      <span className="staking-payout-stat__value">
                        {formatDec(payoutDetail.remaining_amount)} {payoutDetail.currency || ""}
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-calendar-day" />
                      </span>
                      <span className="staking-payout-stat__label">Paid days</span>
                      <span className="staking-payout-stat__value">
                        {payoutDetail.paid_days != null ? String(payoutDetail.paid_days) : "—"}
                        {payoutDetail.duration_days != null ? (
                          <span className="staking-payout-stat__hint"> / {payoutDetail.duration_days}d term</span>
                        ) : null}
                      </span>
                    </div>
                    <div className="staking-payout-stat">
                      <span className="staking-payout-stat__icon" aria-hidden>
                        <i className="fas fa-arrow-circle-right" />
                      </span>
                      <span className="staking-payout-stat__label">Next payout</span>
                      <span className="staking-payout-stat__value">{formatDate(payoutDetail.next_payout_date)}</span>
                    </div>
                  </div>

                  <div className="staking-payout-timeline">
                    <div>
                      <span className="staking-payout-timeline__l">Start</span>
                      <span className="staking-payout-timeline__v">{formatDate(payoutDetail.start_date)}</span>
                    </div>
                    <div>
                      <span className="staking-payout-timeline__l">Maturity</span>
                      <span className="staking-payout-timeline__v">{formatDate(payoutDetail.end_date)}</span>
                    </div>
                    <div>
                      <span className="staking-payout-timeline__l">Last credit</span>
                      <span className="staking-payout-timeline__v">{formatDate(payoutDetail.last_payout_at)}</span>
                    </div>
                  </div>

                  <div className="staking-payout-log-card">
                    <div className="staking-payout-log-card__head">
                      <h3>Daily payout log</h3>
                      <p>Per-day credits; amounts match your plan’s release schedule</p>
                    </div>
                    {Array.isArray(payoutDetail.payout_history) && payoutDetail.payout_history.length > 0 ? (
                      <div className="staking-payout-table-wrap">
                        <table className="staking-payout-table">
                          <thead>
                            <tr>
                              <th>Day</th>
                              <th>Amount</th>
                              <th>Paid at</th>
                              <th>Next after</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payoutDetail.payout_history.map((h) => (
                              <tr key={h._id || `${h.day}-${h.paid_at}`}>
                                <td>
                                  <span className="staking-payout-day-badge">{h.day ?? "—"}</span>
                                </td>
                                <td className="staking-payout-table__amount">
                                  {formatDec(h.amount)} <span className="staking-payout-table__ccy">{payoutDetail.currency || ""}</span>
                                </td>
                                <td>{formatDate(h.paid_at)}</td>
                                <td>{formatDate(h.next_payout_date)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="staking-payout-table-wrap--empty">No payout rows yet.</div>
                    )}
                  </div>

                  <div className="staking-payout-log-card staking-payout-periods-card">
                    <div className="staking-payout-log-card__head">
                      <h3>ROI payout periods</h3>
                      <p>Scheduled ROI payouts — paid or unpaid per period</p>
                    </div>
                    <PayoutPeriodsSummary periods={payoutListRow?.roi_payout_periods} />
                    <PayoutPeriodsTable
                      periods={payoutListRow?.roi_payout_periods}
                      emptyText="No ROI payout periods."
                    />
                  </div>

                  <div className="staking-payout-log-card staking-payout-periods-card">
                    <div className="staking-payout-log-card__head">
                      <h3>Referral payout periods</h3>
                      <p>Scheduled referral commissions — paid or unpaid per period</p>
                    </div>
                    <PayoutPeriodsSummary periods={payoutListRow?.referral_payout_periods} />
                    <PayoutPeriodsTable
                      periods={payoutListRow?.referral_payout_periods}
                      showLevel
                      emptyText="No referral payout periods."
                    />
                  </div>
                      </>
                    );
                  })()}
                </>
              ) : null}
            </div>
            <div className="staking-payout-modal__foot">
              <button type="button" className="staking-payout-btn-close" onClick={closePayoutModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default StakingStakesPage;
